import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ChartBarSquareIcon,
  ClockIcon,
  CursorArrowRaysIcon,
  InformationCircleIcon,
  PresentationChartLineIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import ProfilePicture from '../components/shared/ProfilePicture';

interface Summary {
  active_seconds: number;
  active_users: number;
  sessions: number;
  active_days: number;
  average_seconds_per_user: number;
}

interface TimelineItem {
  day: string;
  active_seconds: number;
  active_users: number;
}

interface PageItem {
  page_path: string;
  page_title: string;
  active_seconds: number;
  active_users: number;
  sessions: number;
  last_seen_at: string;
}

interface UserItem {
  member_id: number;
  vorname: string;
  name: string;
  active_seconds: number;
  page_sessions: number;
  pages_visited: number;
  last_seen_at: string;
}

interface RecentItem {
  member_id: number;
  vorname: string;
  name: string;
  page_path: string;
  page_title: string;
  active_seconds: number;
  last_seen_at: string;
}

interface AnalyticsResponse {
  period_days: number;
  generated_at: string;
  summary: Summary;
  timeline: TimelineItem[];
  pages: PageItem[];
  users: UserItem[];
  recent: RecentItem[];
}

const PERIODS = [
  { days: 7, label: '7 Tage' },
  { days: 30, label: '30 Tage' },
  { days: 90, label: '90 Tage' },
];

const formatDuration = (seconds: number) => {
  const value = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  if (hours > 0) return `${hours} Std ${minutes} Min`;
  if (minutes > 0) return `${minutes} Min`;
  return `${value} Sek`;
};

const formatDate = (value: string, withTime = false) => {
  if (!value) return '–';
  return new Intl.DateTimeFormat('de-DE', withTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { day: '2-digit', month: 'short' }
  ).format(new Date(value));
};

const ActivityChart: React.FC<{ data: TimelineItem[] }> = ({ data }) => {
  const points = useMemo(() => {
    const max = Math.max(...data.map((item) => item.active_seconds), 1);
    return data.map((item, index) => ({
      ...item,
      x: data.length === 1 ? 50 : 4 + (index / (data.length - 1)) * 92,
      y: 92 - (item.active_seconds / max) * 78,
    }));
  }, [data]);

  if (data.length === 0) {
    return <div className="flex h-52 items-center justify-center text-sm text-gray-500 dark:text-gray-400">Noch keine Aktivitätsdaten</div>;
  }

  return (
    <div>
      <svg viewBox="0 0 100 100" className="h-52 w-full overflow-visible" role="img" aria-label="Aktive Nutzungszeit im Verlauf">
        {[20, 44, 68, 92].map((y) => (
          <line key={y} x1="4" x2="96" y1={y} y2={y} className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="0.5" />
        ))}
        <polyline
          points={points.map((point) => `${point.x},${point.y}`).join(' ')}
          fill="none"
          className="stroke-primary-600 dark:stroke-primary-400"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((point) => (
          <circle key={point.day} cx={point.x} cy={point.y} r="2.2" className="fill-white stroke-primary-600 dark:fill-[#1c2432] dark:stroke-primary-400" strokeWidth="1.4">
            <title>{`${formatDate(point.day)}: ${formatDuration(point.active_seconds)}`}</title>
          </circle>
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-[11px] font-semibold text-gray-400 dark:text-gray-500">
        <span>{formatDate(data[0].day)}</span>
        <span>{formatDate(data[data.length - 1].day)}</span>
      </div>
    </div>
  );
};

const UserActivityAnalytics: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/api/user-activity/analytics', { params: { days } });
      setData(response.data);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Die Nutzungsdaten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const maxPageSeconds = Math.max(...(data?.pages.map((page) => page.active_seconds) || []), 1);

  const summaryCards = [
    { label: 'Aktive Zeit', value: formatDuration(data?.summary.active_seconds || 0), icon: ClockIcon, tone: 'text-primary-600 bg-primary-50 dark:bg-primary-900/20 dark:text-primary-400' },
    { label: 'Aktive Nutzer', value: String(data?.summary.active_users || 0), icon: UserGroupIcon, tone: 'text-success-600 bg-success-50 dark:bg-success-900/20 dark:text-success-400' },
    { label: 'Sitzungen', value: String(data?.summary.sessions || 0), icon: CursorArrowRaysIcon, tone: 'text-info-600 bg-info-50 dark:bg-info-900/20 dark:text-info-400' },
    { label: 'Ø je Nutzer', value: formatDuration(data?.summary.average_seconds_per_user || 0), icon: PresentationChartLineIcon, tone: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400' },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-7">
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-primary-600 dark:text-primary-400">
            <ChartBarSquareIcon className="h-4 w-4" />
            Nur Vorstand
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-[28px]">Nutzungsanalyse</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Aktive Portalzeit nach Mitglied und besuchter Seite</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-[#1c2432]" aria-label="Auswertungszeitraum">
            {PERIODS.map((period) => (
              <button
                key={period.days}
                type="button"
                onClick={() => setDays(period.days)}
                className={`min-h-9 rounded-lg px-3 text-xs font-bold transition-colors ${days === period.days
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                {period.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:opacity-50 dark:border-gray-700 dark:bg-[#1c2432] dark:text-gray-300"
            aria-label="Daten neu laden"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <div className="mb-6 flex gap-3 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-900 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-100">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-none text-primary-600 dark:text-primary-400" />
        <p>Gezählt wird nur, wenn das Portal sichtbar ist und innerhalb der letzten Minute bedient wurde. Eingaben und Klickinhalte werden nicht erfasst.</p>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm font-semibold text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300" role="alert">
          {error}
        </div>
      )}

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Kennzahlen">
        {summaryCards.map(({ label, value, icon: Icon, tone }) => (
          <div key={label} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:p-5">
            <div className={`mb-4 flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
              <Icon className="h-5 w-5" />
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">{label}</p>
            <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-2xl">{loading && !data ? '–' : value}</p>
          </div>
        ))}
      </section>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.65fr)_minmax(280px,0.75fr)]">
        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-bold text-gray-900 dark:text-white">Aktive Zeit im Verlauf</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Summierte aktive Sekunden pro Tag</p>
            </div>
            {data?.generated_at && <span className="text-[11px] text-gray-400">Stand {formatDate(data.generated_at, true)}</span>}
          </div>
          <ActivityChart data={data?.timeline || []} />
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:p-6">
          <h2 className="font-bold text-gray-900 dark:text-white">Zuletzt aktiv</h2>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">Jüngste Seitenaktivitäten</p>
          <div className="space-y-3">
            {(data?.recent || []).slice(0, 6).map((item, index) => (
              <div key={`${item.member_id}-${item.page_path}-${index}`} className="flex items-center gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0 dark:border-gray-700">
                <ProfilePicture user={{ id: item.member_id, vorname: item.vorname, name: item.name }} name={`${item.vorname} ${item.name}`} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{item.vorname} {item.name}</p>
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">{item.page_title || item.page_path}</p>
                </div>
                <time className="text-[11px] text-gray-400">{formatDate(item.last_seen_at, true)}</time>
              </div>
            ))}
            {!loading && (data?.recent.length || 0) === 0 && <p className="py-8 text-center text-sm text-gray-500">Noch keine Daten</p>}
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:p-6">
        <div className="mb-5">
          <h2 className="font-bold text-gray-900 dark:text-white">Seiten nach aktiver Zeit</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Welche Bereiche tatsächlich genutzt werden</p>
        </div>
        <div className="space-y-4">
          {(data?.pages || []).map((page, index) => (
            <div key={page.page_path} className="grid gap-2 sm:grid-cols-[minmax(180px,0.8fr)_minmax(220px,1.4fr)_auto] sm:items-center sm:gap-5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="w-5 text-xs font-bold tabular-nums text-gray-300 dark:text-gray-600">{String(index + 1).padStart(2, '0')}</span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{page.page_title || page.page_path}</p>
                    <p className="truncate text-[11px] text-gray-400">{page.page_path}</p>
                  </div>
                </div>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className="h-full rounded-full bg-primary-600 dark:bg-primary-400" style={{ width: `${Math.max(2, (page.active_seconds / maxPageSeconds) * 100)}%` }} />
              </div>
              <div className="flex items-center justify-between gap-4 text-xs sm:min-w-44 sm:justify-end">
                <span className="text-gray-500 dark:text-gray-400">{page.active_users} Nutzer</span>
                <strong className="tabular-nums text-gray-900 dark:text-white">{formatDuration(page.active_seconds)}</strong>
              </div>
            </div>
          ))}
          {!loading && (data?.pages.length || 0) === 0 && <p className="py-10 text-center text-sm text-gray-500">Im gewählten Zeitraum liegen noch keine Daten vor.</p>}
        </div>
      </section>

      <section className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="p-4 sm:p-6">
          <h2 className="font-bold text-gray-900 dark:text-white">Aktivität je Mitglied</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Sortiert nach aktiver Zeit im gewählten Zeitraum</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-left">
            <thead className="border-y border-gray-200 bg-gray-50 text-[11px] uppercase tracking-[0.1em] text-gray-400 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 font-bold sm:px-6">Mitglied</th>
                <th className="px-4 py-3 font-bold">Aktive Zeit</th>
                <th className="px-4 py-3 font-bold">Seiten</th>
                <th className="px-4 py-3 font-bold">Seiten-Sitzungen</th>
                <th className="px-4 py-3 font-bold sm:pr-6">Zuletzt aktiv</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {(data?.users || []).map((user) => (
                <tr key={user.member_id} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="px-4 py-3 sm:px-6">
                    <div className="flex items-center gap-3">
                      <ProfilePicture user={{ id: user.member_id, vorname: user.vorname, name: user.name }} name={`${user.vorname} ${user.name}`} size="sm" />
                      <div>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{user.vorname} {user.name}</p>
                        <p className="text-[11px] text-gray-400">Mitglied #{user.member_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-bold tabular-nums text-gray-900 dark:text-white">{formatDuration(user.active_seconds)}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-300">{user.pages_visited}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-300">{user.page_sessions}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 sm:pr-6">{formatDate(user.last_seen_at, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && (data?.users.length || 0) === 0 && <p className="py-10 text-center text-sm text-gray-500">Keine Mitgliederaktivität im gewählten Zeitraum.</p>}
        </div>
      </section>
    </div>
  );
};

export default UserActivityAnalytics;
